import type { KeyboardEvent } from 'react';
import debounce from 'lodash/debounce';
import TagManager from 'react-gtm-module';
import { MagnifyingGlass, X } from '@phosphor-icons/react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

import type { GetSearchResultResponse } from '@tmh-website/services/search.client.service';
import CollapseContainer from '@tmh-website/components/shared/CollapseContainer';
import LoadingSpinner from '@tmh-website/ui/LoadingSpinner';
import Popup from '@tmh-website/ui/Popup';
import theme from '@tmh-website/styles/theme';
import { Color } from '@tmh-website/shared/colors';
import { getSearchResult, isProjectWithSearch } from '@tmh-website/services/search.client.service';
import { LocalePrefixToURLPathsMap } from '@tmh-website/shared/cms';
import { ProjectContext, useIsMobile } from '@tmh-website/shared';
import { toggleTrustBadge } from '@tmh-website/services/floating-ui.service';
import { type LocalePrefix, hasShop } from '@tmh-website/shared/const';
import { type SearchResultContent, SearchResultType } from '@tmh-website/services/search.service';
import { useSearchAtom } from '@tmh-website/shared/hooks';

import * as Styled from './Search.styles';
import { ResultsEntryContent, ResultsEntryProduct, ResultsEntrySkeleton } from './ResultEntries';

export interface SearchFakeData {
  query: string;
  results: GetSearchResultResponse;
}

/**
 * The main search component
 */
interface SearchProps {
  locale: LocalePrefix;
  /**
   * Pass fake data to the search component. This is for testing purpose in Storybook only
   */
  searchFakeData?: SearchFakeData;
  /**
   * Enable this flag when this component is used outside of the Next.js context.
   */
  isStandaloneMode?: boolean;
}

const Search = ({ locale, searchFakeData, isStandaloneMode }: SearchProps) => {
  const { project } = useContext(ProjectContext);

  if (!isProjectWithSearch(project)) {
    throw new Error(`The '${project}' project does not support search functionality!`);
  }

  const { t } = useTranslation();
  const [query, setQuery] = useState(searchFakeData ? searchFakeData.query : '');
  const [searchFieldValue, setSearchFieldValue] = useState(searchFakeData ? searchFakeData.query : '');
  const hasShopAvailable = hasShop(locale, project);
  const [isSearching, setIsSearching] = useState(false); // New state to track search status
  const isMobile = useIsMobile();
  const { textOpacity } = useTheme();
  const searchAtom = useSearchAtom();

  const maxProductsPerPage = isMobile ? 4 : 6;
  const maxContentEntriesPerPage = isMobile ? 2 : 3;

  /**
   * Do not search immediately after each keystroke
   */
  const debouncedSetQuery = useCallback(debounce(setQuery, 400), []);

  useEffect(() => {
    debouncedSetQuery(searchFieldValue);
  }, [searchFieldValue]);

  useEffect(() => {
    if (!searchAtom.isOpen) {
      setSearchFieldValue(searchFakeData ? searchFakeData.query : '');
    }

    // Hide Trustbadge when search is open
    if (isMobile) {
      toggleTrustBadge(!searchAtom.isOpen);
    }
  }, [searchAtom.isOpen, isMobile]);

  /**
   * Get search data
   */
  const { data: searchResults, isFetching } = useQuery(
    ['search', locale, query],
    () => {
      if (query) {
        TagManager.dataLayer({ dataLayer: { event: 'search_term', search_term: query } });
      }
      return getSearchResult(query, locale, project, hasShopAvailable);
    },
    {
      keepPreviousData: true,
      initialData: searchFakeData ? searchFakeData.results : undefined,
      enabled: searchFakeData === undefined,
      onSuccess: () => {
        setIsSearching(false);
      },
    }
  );

  function redirectToSearchPage() {
    if (query.length >= 3) {
      searchAtom.close();
      // We can't use next router here because it does not work in the Magento standalone mode
      window.location.href = `/${locale}/${LocalePrefixToURLPathsMap[locale].search}?q=${query}`;
    }
  }

  /**
   * navigate to the search page
   */
  const handleKeyPress = debounce((e: KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Enter' || e.code === 'Enter' || e.keyCode === 13) && !isSearching) {
      redirectToSearchPage();
    }
  }, 500);

  const contentResults: SearchResultContent[] = searchResults
    ? Object.keys(searchResults)
        .map(key => {
          if (key !== SearchResultType.Product && key !== SearchResultType.Term) {
            return searchResults[key];
          }
          return undefined;
        })
        .flat()
        .filter(e => e !== undefined)
        .sort((a, b) => b.score - a.score)
    : [];
  const hasProductResults = !!searchResults?.[SearchResultType.Product].length;
  const hasContentResults = !!contentResults.length;

  return (
    <Popup
      themeColor={Color.Neutral_0_White}
      isOpen={searchAtom.isOpen}
      onClose={searchAtom.close}
      noPadding
      maxWidth={searchFieldValue !== '' ? '1000px' : undefined}
      isStandaloneMode={!!isStandaloneMode}>
      <Styled.HeaderContainer>
        <Styled.SearchField $themeColor={Color.Neutral_0_White}>
          <Styled.IconDesktop>
            {isFetching ? <LoadingSpinner size={24} /> : <MagnifyingGlass size={24} style={{ marginBottom: '-3px' }} />}
          </Styled.IconDesktop>
          <Styled.SearchInput
            name='search'
            id='search'
            value={searchFieldValue}
            placeholder={t('global:search.inputPlaceholder') as string}
            onChange={e => setSearchFieldValue(e.currentTarget.value)}
            onKeyDown={handleKeyPress}
            onFocus={() => {
              setIsSearching(true);
              searchAtom.open();
            }}
            autoFocus
          />
          <Styled.XButton
            themeColor={Color.Neutral_0_White}
            phosphorPadding
            onClick={() => setSearchFieldValue('')}
            disabled={query.length === 0}>
            <X size={20} color={theme.color[Color.Blue_1000_Deep]} />
          </Styled.XButton>
          <Styled.ShowAllButton type='button' onClick={redirectToSearchPage} disabled={query.length < 3}>
            <Styled.ShowAllButtonText>{t('global:showAll')}</Styled.ShowAllButtonText>
            <Styled.ShowAllButtonIcon>
              {isFetching ? <LoadingSpinner size={22} color={Color.Neutral_0_White} /> : <MagnifyingGlass size={22} />}
            </Styled.ShowAllButtonIcon>
          </Styled.ShowAllButton>
        </Styled.SearchField>
      </Styled.HeaderContainer>
      <CollapseContainer isOpen={searchFieldValue !== ''} displayValueWhenVisible='flex'>
        <Styled.ResultsContainer>
          {searchFieldValue.length < 3 ? (
            // Enter at least 3 characters to search
            <Styled.ErrorMessage $style='uiText' as='p' $opacity={textOpacity} $color={Color.Blue_1000_Deep}>
              {t('global:search.noEnoughCharacters')}
            </Styled.ErrorMessage>
          ) : (
            <>
              {/* Hide all result containers when no products and no pages were found */}
              {!isFetching && !hasProductResults && !hasContentResults && (
                <Styled.ErrorMessage $style='uiText' as='p' $opacity={textOpacity} $color={Color.Blue_1000_Deep}>
                  {t('global:search.noResults')}
                </Styled.ErrorMessage>
              )}
              {isFetching && (
                <>
                  <ResultsEntrySkeleton maxWidth={400} />
                  <ResultsEntrySkeleton maxWidth={500} />
                  <ResultsEntrySkeleton maxWidth={320} />
                  <ResultsEntrySkeleton maxWidth={380} />
                </>
              )}
              {/* Results */}
              {!isFetching && (
                <>
                  {searchResults?.[SearchResultType.Product].slice(0, maxProductsPerPage).map(entry => (
                    <ResultsEntryProduct key={entry.url} product={entry} locale={locale} />
                  ))}
                  {hasContentResults && hasProductResults && <Styled.Divider />}
                  {contentResults.slice(0, maxContentEntriesPerPage).map(entry => (
                    <ResultsEntryContent key={entry.path} locale={locale} result={entry} />
                  ))}
                  {!isMobile && <Styled.ResultsFade />}
                </>
              )}
            </>
          )}
        </Styled.ResultsContainer>
      </CollapseContainer>
    </Popup>
  );
};

export default Search;
